/**
 * The days an event runs on (GS-03: a multi-day event shows on every one of its days).
 *
 * Source of truth, in order:
 *   1. the event's `event_days` rows, when it is in the MSR date model (the server keeps them equal to its schedule);
 *   2. otherwise the same plan derived here from start/end in the EVENT's time zone. This mirrors the server's
 *      `_event_day_plan_of` (migration 2026-10-04-200): one service day per local date from the start date to the end
 *      date, each day's window running from the start's local time to the end's local time (overnight when the end
 *      time is at or before the start time), clipped to the event's start and end; a day whose window would START at
 *      or after the event's end is not a day; an event with no end is one day.
 *
 * Pure (no Date.now) so it is unit-tested directly.
 */

export const DEFAULT_EVENT_TZ = "America/Chicago";

/** One service day: its local date and, when known, its window as ISO instants. */
export interface ServiceDay {
  date: string; // YYYY-MM-DD, event-local
  windowStart: string | null;
  windowEnd: string | null;
}

export interface ScheduleInput {
  start_time?: string | null;
  end_time?: string | null;
  timezone?: string | null;
  date_tbd?: boolean | null;
  start_date?: string | null;
}

/** The time zone the server would use for this event (blank or invalid = Central). */
export function eventZone(tz: string | null | undefined): string {
  const t = (tz ?? "").trim();
  if (!t) {
    return DEFAULT_EVENT_TZ;
  }
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: t });
    return t;
  } catch {
    return DEFAULT_EVENT_TZ;
  }
}

interface LocalParts {
  date: string; // YYYY-MM-DD
  seconds: number; // seconds since local midnight
}

const FORMATTERS = new Map<string, Intl.DateTimeFormat>();

/** One formatter per zone (zonedLocalToInstant reads offsets 3-4 times per call; internal audit P3-5). */
function formatterFor(tz: string): Intl.DateTimeFormat {
  let f = FORMATTERS.get(tz);
  if (!f) {
    f = new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      hourCycle: "h23",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
    FORMATTERS.set(tz, f);
  }
  return f;
}

function localParts(d: Date, tz: string): LocalParts {
  const f = formatterFor(tz).formatToParts(d);
  const get = (t: string) => f.find((p) => p.type === t)?.value ?? "00";
  const hour = Number(get("hour")) % 24;
  return {
    date: `${get("year")}-${get("month")}-${get("day")}`,
    // fractional seconds kept (time zone offsets are whole minutes, so the UTC milliseconds are the local ones):
    // the server compares exact times-of-day, so 21:24:09.041 must not round down to 21:24:09
    seconds:
      hour * 3600 +
      Number(get("minute")) * 60 +
      Number(get("second")) +
      d.getUTCMilliseconds() / 1000,
  };
}

/** Offset (ms) of `tz` from UTC at instant `t`. */
function offsetMs(t: number, tz: string): number {
  const p = localParts(new Date(t), tz);
  const [y, m, d] = p.date.split("-").map(Number);
  const asUtc = Date.UTC(y, m - 1, d) + Math.round(p.seconds * 1000);
  return asUtc - t;
}

const DAY_MS = 86400000;

/**
 * The instant of a local wall-clock time (date + seconds since midnight) in `tz`, resolved EXACTLY as PostgreSQL's
 * `(date + time)::timestamp AT TIME ZONE tz` resolves it (the server's `_event_day_plan_of` uses that cast):
 *   - an ordinary time has one instant;
 *   - a REPEATED time (fall-back fold, e.g. Chicago 2026-11-01 01:30) takes the offset in force AFTER the
 *     transition, i.e. the LATER instant (01:30 CST = 07:30Z, not 01:30 CDT = 06:30Z);
 *   - a NONEXISTENT time (spring-forward gap, e.g. Chicago 2027-03-14 02:15) takes the offset in force BEFORE the
 *     transition, which moves it FORWARD (02:15 CST = 08:15Z, shown as 03:15 CDT).
 * The candidate offsets are those in force a day either side of the wall time; a zone never changes its offset twice
 * within that span, so the true offset is one of the two. (External audit 2026-10-12, gaps 1-2: the earlier
 * two-guess loop picked the earlier instant in a fold and moved a gap time backward, which dropped a service day.)
 */
export function zonedLocalToInstant(
  date: string,
  seconds: number,
  tz: string,
): number {
  const [y, m, d] = date.split("-").map(Number);
  const wall = Date.UTC(y, m - 1, d) + Math.round(seconds * 1000);
  const before = offsetMs(wall - DAY_MS, tz);
  const after = offsetMs(wall + DAY_MS, tz);
  let best: number | null = null;
  for (const off of before === after ? [before] : [before, after]) {
    const t = wall - off;
    if (offsetMs(t, tz) === off && (best === null || t > best)) {
      best = t; // a valid reading; in a fold both are valid and the later one wins
    }
  }
  return best ?? wall - before; // a gap: no reading is valid, use the offset before the transition
}

/**
 * Browser-local midnight at the START of the day after `ymd` — the exclusive end of an all-day calendar bar whose
 * last (inclusive) day is `ymd`. Built from calendar fields, never `midnight + 86400000`: across a DST change that
 * lands at 23:00 (fall) or 01:00 the next day (spring), and the calendar drew one day short or one day long.
 */
export function localMidnightAfter(ymd: string): Date {
  const [y, m, d] = ymd.split("-").map(Number);
  return new Date(y, m - 1, d + 1);
}

/** YYYY-MM-DD plus n days. */
export function addDaysYmd(date: string, n: number): string {
  const [y, m, d] = date.split("-").map(Number);
  const t = new Date(Date.UTC(y, m - 1, d + n));
  return t.toISOString().slice(0, 10);
}

function daysBetween(a: string, b: string): number {
  const [ay, am, ad] = a.split("-").map(Number);
  const [by, bm, bd] = b.split("-").map(Number);
  return Math.round(
    (Date.UTC(by, bm - 1, bd) - Date.UTC(ay, am - 1, ad)) / 86400000,
  );
}

/** The server's day plan, derived from the event's schedule (see file header). [] = date TBD / no start / bad data. */
export function deriveServiceDays(ev: ScheduleInput): ServiceDay[] {
  // A date-TBD event stores a 9999-12-31 placeholder start (EventForm's TBD_PLACEHOLDER_DATE).
  if (
    ev.date_tbd === true ||
    !ev.start_time ||
    ev.start_time.startsWith("9999")
  ) {
    return [];
  }
  const start = Date.parse(ev.start_time);
  if (Number.isNaN(start)) {
    return [];
  }
  const tz = eventZone(ev.timezone);
  const s = localParts(new Date(start), tz);
  let end = ev.end_time ? Date.parse(ev.end_time) : NaN;
  if (Number.isNaN(end)) {
    end = zonedLocalToInstant(addDaysYmd(s.date, 1), 0, tz);
  }
  if (end <= start) {
    return [];
  }
  const e = localParts(new Date(end), tz);
  // The server flags a span of 17+ calendar steps (or more than 14 days) instead of planning it.
  if (daysBetween(s.date, e.date) >= 17) {
    return [];
  }
  const open = s.seconds;
  const close = e.seconds;
  const out: ServiceDay[] = [];
  for (let d = s.date; d <= e.date; d = addDaysYmd(d, 1)) {
    const d2 = close <= open ? addDaysYmd(d, 1) : d;
    const rawS = zonedLocalToInstant(d, open, tz);
    const rawE = zonedLocalToInstant(d2, close, tz);
    if (rawS >= end) {
      break;
    }
    if (rawE <= rawS) {
      continue; // the local clock reversed across a DST gap: the server does not plan that day either
    }
    const ws = Math.max(rawS, start);
    const we = Math.min(rawE, end);
    if (we > ws) {
      out.push({
        date: d,
        windowStart: new Date(ws).toISOString(),
        windowEnd: new Date(we).toISOString(),
      });
    }
  }
  return out.length > 14 ? [] : out;
}

/** An `event_days` row as read from the table. */
export interface EventDayRow {
  id?: string;
  event_id: string;
  service_date: string;
  window_start?: string | null;
  window_end?: string | null;
}

/** event_days rows grouped per event, each list sorted by date. */
export function groupEventDays(rows: EventDayRow[]): Map<string, ServiceDay[]> {
  const out = new Map<string, ServiceDay[]>();
  for (const r of rows) {
    const list = out.get(r.event_id) ?? [];
    list.push({
      date: r.service_date,
      windowStart: r.window_start ?? null,
      windowEnd: r.window_end ?? null,
    });
    out.set(r.event_id, list);
  }
  for (const list of out.values()) {
    list.sort((a, b) => a.date.localeCompare(b.date));
  }
  return out;
}

/** The event's days: its event_days rows when it has any (date model), else derived from its schedule. */
export function serviceDaysOf(
  ev: ScheduleInput,
  modelDays: ServiceDay[] | undefined,
): ServiceDay[] {
  if (modelDays && modelDays.length > 0) {
    return modelDays;
  }
  return deriveServiceDays(ev);
}

/** One event on one of its days, with its place in the event ("Day 2 of 3"). */
export interface EventDayEntry<T> {
  row: T;
  date: string; // "" = date TBD
  dayIndex: number; // 1-based; 1 for a one-day or TBD event
  dayCount: number; // 1 for a one-day or TBD event
  windowStart: string | null;
  windowEnd: string | null;
}

/** "Day 1 of 2", or "" for a one-day event. */
export function dayOfLabel(e: { dayIndex: number; dayCount: number }): string {
  return e.dayCount > 1 ? `Day ${e.dayIndex} of ${e.dayCount}` : "";
}

/**
 * Expand events into one entry per day they run. `repDays` (YYYY-MM-DD list) keeps only the days a per-day rep works;
 * null/empty = every day. The day numbers always count the EVENT's days ("Day 2 of 2" even if the rep works only that
 * day). An event with no plannable days (TBD) yields one entry under its start date, or "" when it has none.
 */
export function expandEventDays<T extends ScheduleInput>(
  row: T,
  modelDays: ServiceDay[] | undefined,
  repDays?: string[] | null,
): EventDayEntry<T>[] {
  const days = serviceDaysOf(row, modelDays);
  if (days.length === 0) {
    const date =
      row.date_tbd === true || (row.start_time ?? "").startsWith("9999")
        ? ""
        : (row.start_date ??
          (row.start_time ? (deriveFallbackDate(row) ?? "") : ""));
    return [
      {
        row,
        date,
        dayIndex: 1,
        dayCount: 1,
        windowStart: row.start_time ?? null,
        windowEnd: row.end_time ?? null,
      },
    ];
  }
  const keep = repDays && repDays.length > 0 ? new Set(repDays) : null;
  const out: EventDayEntry<T>[] = [];
  days.forEach((d, i) => {
    if (keep && !keep.has(d.date)) {
      return;
    }
    out.push({
      row,
      date: d.date,
      dayIndex: i + 1,
      dayCount: days.length,
      windowStart: d.windowStart,
      windowEnd: d.windowEnd,
    });
  });
  return out;
}

function deriveFallbackDate(row: ScheduleInput): string | null {
  const t = row.start_time ? Date.parse(row.start_time) : NaN;
  if (Number.isNaN(t)) {
    return null;
  }
  return localParts(new Date(t), eventZone(row.timezone)).date;
}

function stillRunning<T extends ScheduleInput>(
  e: EventDayEntry<T>,
  today: string,
  now: number,
): boolean {
  const end = e.windowEnd ? Date.parse(e.windowEnd) : NaN;
  if (Number.isNaN(end)) {
    return false;
  }
  if (end > now) {
    return true;
  }
  return localParts(new Date(end), eventZone(e.row.timezone)).date >= today;
}

/**
 * The MSR schedule's entries: every event on each day it runs (only the rep's days for a per-day rep). Without
 * `includePast`, days before `today` are left out (the running day of a gun show still shows, under its own date).
 */
export function scheduleDayEntries<
  T extends ScheduleInput & {
    event_id: string;
    sales_rep_days?: string[] | null;
  },
>(
  rows: T[],
  daysByEvent: Map<string, ServiceDay[]>,
  today: string,
  includePast: boolean,
  now: number = Date.now(),
): EventDayEntry<T>[] {
  const out: EventDayEntry<T>[] = [];
  for (const row of rows) {
    for (const e of expandEventDays(
      row,
      daysByEvent.get(row.event_id),
      row.sales_rep_days,
    )) {
      // A day stays while it is still running: its window has not ended, or it ends (event-local) today or later.
      // An overnight range night (Mon 7pm - Tue 1am) is dated Mon but still listed on Tue, as get_msr_schedule keeps
      // it; a phone ahead of the event's zone still sees a day that is running (internal audit P2-1).
      if (
        !includePast &&
        e.date !== "" &&
        e.date < today &&
        !stillRunning(e, today, now)
      ) {
        continue;
      }
      out.push(e);
    }
  }
  return out;
}
